import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import ComplianceFooter from "@/components/landing/ComplianceFooter";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

/**
 * Closed-loop password reset using one of the 10 recovery codes issued at
 * signup. Hits `POST /api/auth/reset-with-recovery-code`. On success we
 * show a confirmation with the number of codes remaining, then route the
 * user back to /login.
 */
export default function RecoverWithCodes() {
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(null); // { codes_remaining }
    const nav = useNavigate();

    async function submit(e) {
        e.preventDefault();
        setErr(null);
        setBusy(true);
        try {
            const { data } = await api.post("/auth/reset-with-recovery-code", {
                email: email.trim().toLowerCase(),
                recovery_code: code.trim(),
                new_password: password,
            });
            setDone({ codes_remaining: data?.codes_remaining ?? null });
            toast.success("Password reset. Sign in with your new password.");
        } catch (e2) {
            setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Invalid email or recovery code.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-sm">
                    <Link to="/" className="flex items-center gap-2 mb-8" data-testid="recover-logo">
                        <div className="w-8 h-8 rounded bg-primary text-primary-foreground flex items-center justify-center font-inter font-extrabold text-xs">MD</div>
                        <div className="font-inter font-extrabold tracking-tight text-lg">Melian&nbsp;Dialogue<span className="text-primary">.</span></div>
                    </Link>

                    {done ? (
                        <div data-testid="recover-success-view">
                            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-5 h-5" strokeWidth={2.25} />
                            </div>
                            <div className="overline mb-3 text-primary">Recovery complete</div>
                            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-[1.1] mb-2">
                                Password <span className="text-primary">reset</span>.
                            </h1>
                            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-6">
                                You can now sign in with your new password.
                                {typeof done.codes_remaining === "number" && (
                                    <> You have <b className="text-foreground">{done.codes_remaining}</b> recovery code{done.codes_remaining === 1 ? "" : "s"} left.</>
                                )}
                            </p>
                            <Button
                                type="button"
                                className="w-full"
                                data-testid="recover-goto-login"
                                onClick={() => nav("/login")}
                            >
                                Sign in
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                                <KeyRound className="w-5 h-5" strokeWidth={2.25} />
                            </div>
                            <div className="overline mb-3 text-primary">Account recovery</div>
                            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-[1.1] mb-2">
                                Use a <span className="text-primary">recovery code</span>.
                            </h1>
                            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-6">
                                This platform is closed-loop — we don't send password-reset emails. Enter your
                                email + one of the <b className="text-foreground">10 single-use recovery codes</b>{" "}
                                we gave you at signup, then set a new password. Each code burns after one use.
                            </p>

                            <form onSubmit={submit} className="space-y-4" data-testid="recover-form">
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="recover-email"
                                        autoComplete="email"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="code">Recovery code</Label>
                                    <Input
                                        id="code"
                                        type="text"
                                        required
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        data-testid="recover-code"
                                        placeholder="e.g. A7QK-92LM"
                                        autoComplete="off"
                                        spellCheck={false}
                                        className="font-mono uppercase tracking-tight"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="password">New password</Label>
                                    <PasswordInput
                                        id="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        testId="recover-password"
                                        toggleTestId="recover-password-toggle"
                                        autoComplete="new-password"
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-1">Minimum 8 characters.</p>
                                </div>

                                {err && (
                                    <div className="text-danger text-sm" data-testid="recover-error">
                                        {err}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={busy}
                                    data-testid="recover-submit"
                                >
                                    {busy ? "Resetting…" : "Reset password"}
                                </Button>
                            </form>

                            <div className="mt-6 rounded-md border border-dashed border-border p-3">
                                <div className="text-xs text-muted-foreground">
                                    <b className="text-foreground">Lost every code?</b> Contact{" "}
                                    <Link to="/contact" className="text-primary hover:underline"
                                          data-testid="recover-contact-link">
                                        support
                                    </Link>{" "}— we'll verify your identity manually.
                                </div>
                            </div>

                            <div className="mt-4 text-xs text-muted-foreground">
                                Remembered it? <Link to="/login" className="text-primary hover:underline"
                                                   data-testid="recover-back-to-login">
                                    Back to sign in
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ComplianceFooter />
        </div>
    );
}
